import type { FC } from 'react';
import dp6 from './../../../resources/content/dp6.png';
import { LinearDPWrapper } from './LinearDP.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface LinearDPProps {}

const LinearDP: FC<LinearDPProps> = () => (
 <LinearDPWrapper>
    <header className="entry-header">
      <h1 className="entry-title" itemProp="headline">Linear Dynamic Programming</h1>			
   </header>
   <br/>
   <br/>
   <div className="entry-content clear" itemProp="text"  style={{height: 'auto !important'}}>
      <p className="text-align-center">Think of it as a systematic approach to solving complex problems by breaking them down into simpler, manageable pieces.</p>
      
      <figure className="aligncenter size-large">
        <img decoding="async" width="775" height="435" src={dp6} alt="" className="lazy-hidden" sizes="auto, (max-width: 775px) 100vw, 775px" />
      </figure>
      
      <ul className="text-align-justify">
        <li className="linear"><strong>Identify Subproblems</strong></li>
        <ul className="text-align-justify">
          <li>This is where we break down our main problem into smaller, similar problems. Think of it like organizing a large group of people into smaller teams ~ each team represents a subproblem that's easier to handle than the entire group.</li>
        </ul>
        <li className="linear"><strong>Define Recurrence Relations</strong></li>
        <ul className="text-align-justify">
          <li>This step is crucial because it establishes how our subproblems connect to each other. We're essentially creating a mathematical formula that shows how the solution to one subproblem depends on the solutions to smaller subproblems.</li>
        </ul>
        <li className="linear"><strong>Solve Subproblems</strong></li>
        <ul className="text-align-justify">
          <li>We start with the smallest, most basic cases and work our way up. This bottom-up approach ensures that when we need a solution to a subproblem, we've already calculated it.</li>
        </ul>
        <li className="linear"><strong>Combine Solutions</strong></li>
        <ul className="text-align-justify">
          <li>Finally, we **combine solutions** from our subproblems to arrive at the answer to our original problem. This is where all our careful preparation pays off ~ we can efficiently construct the final solution using the results we've already computed.</li>
        </ul>
      </ul>
      <br/>
      <h4 className="text-align-left">Fibonacci Numbers</h4>
      <ul className="text-align-justify">
        <li>The Fibonacci sequence is a series of numbers where each number is the sum of the two preceding ones. </li>
        <li>Our goal is to write a program that generates the first N numbers in the Fibonacci sequence.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'n = 5\n'+
            '\n'+
            'Calculate F(5):\n'+
            '	F(0) = 0\n'+
            '	F(1) = 1\n'+
            '	F(2) = 0 + 1 = 1\n'+
            '	F(3) = 1 + 1 = 2\n'+
            '	F(4) = 1 + 2 = 3\n'+
            '	F(5) = 2 + 3 = 5\n'+
            '\n'+
            '👉 F(5) = 5'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Climbing Stairs</h4>
      <ul className="text-align-justify">
        <li>The climbing stairs problem asks: "In how many ways can you reach step i?" </li>
        <li>Think about it this way: at any given step, you have two choices:</li>
        <ul className="text-align-justify">
          <li>Climb 1 step &nbsp;~ Takes longer but gives you more control over your pace</li>
          <li>Climb 2 steps ~ Faster but requires more effort</li>
        </ul>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'n = 3 steps\n'+
            '\n'+
            'Possible options:\n'+
            '	1 + 1 + 1\n'+
            '	1 + 2\n'+
            '	2 + 1\n'+
            '\n'+
            '👉 Total ways: 3 ways'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">House Robber</h4>
      <ul className="text-align-justify">
        <li>The house robber problem introduces constraints ~ you can't rob adjacent houses.</li>
        <li>The key insight is that at each house, we have two choices: rob this house or skip it.</li>
        <li>Your goal is to determine the maximum amount of money you can rob tonight.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'houses = [2, 7, 9, 3, 1]\n'+
            '		\n'+
            'House 0 → 2\n'+
            'House 1 → max(2, 7) = 7\n'+
            'House 2 → max(7, 2 + 9) = 11\n'+
            'House 3 → max(11, 7 + 3) = 11\n'+
            'House 4 → max(11, 11 + 1) = 12\n'+
            '\n'+
            '👉 Final Answer is 12 with Rob houses: [2, 9, 1]'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Jump Game</h4>
      <ul className="text-align-justify">
        <li>You have an array of nums, where each index tells you: “From here, how far can I jump?”. </li>
        <li>Think of it like this: if we can reach a position that can reach the end, then our current position can also reach the end!</li>
        <li>Our goal is to determine if we can reach the last index.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'nums = [2, 3, 1, 1, 4]\n'+
            '\n'+
            'Initialize	maxReach = 0\n'+
            'From index 0 → can reach up to 0 + 2 = 2\n'+
            '	maxReach = 2\n'+
            'From index 1 → can reach 1 + 3 = 4\n'+
            '	maxReach = 4\n'+
            'From index 2 → can reach 2 + 1 = 3\n'+
            '	maxReach = 4 (no change)\n'+
            'From index 3 → can reach 3 + 1 = 4\n'+
            '	maxReach = 4 (no change)\n'+
            'From index 4 → Already reachable!\n'+
            '\n'+
            '👉 Answer is YES (you can reach the end)'
          }
        </SyntaxHighlighter>
      </ul>

   </div>
 </LinearDPWrapper>
);

export default LinearDP;
